import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import type { Recipe } from "../models/Recipe";
import { useRecipes } from "../hooks/useRecipes";
import {
	useCreateRecipeEmpty,
	useDeleteRecipe,
	useDuplicateRecipe,
	useUpsertRecipe,
} from "../hooks/useRecipeMutation";
import { RecipesTable } from "./RecipesTable";
import { RecipeDetailViewer } from "./RecipeDetailViewer";
import { RecipesToolbar } from "./RecipesToolbar";
import { AddRecipeModal } from "./AddRecipeModal";
import { DuplicateRecipeModal } from "./DuplicateRecipeModal";
import { SHOW_ONLY_ACTIVES_DEFAULT } from "@/controls/filters/ShowOnlyActivesCheckbox";
import {
	urlParam,
	useDropUnknownValue,
	useUrlState,
	useUrlText,
} from "@/shared/hooks/useUrlState";
import { useUrlSelection } from "@/shared/hooks/useUrlSelection";

const DEFAULT_LAYOUT = { left: 55, right: 45 };

const URL_SCHEMA = {
	search: urlParam.string(),
	onlyActives: urlParam.bool(SHOW_ONLY_ACTIVES_DEFAULT),
	productType: urlParam.string(),
	productSizeId: urlParam.int(),
	recipeId: urlParam.int(),
};

export const RecipesManager = () => {
	// Filters and the selected record, all in the query string.
	const [
		{ search, onlyActives, productType, productSizeId, recipeId },
		setFilters,
		{ dropValues, resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	// The box types locally and the URL follows once typing stops.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [recipeToDelete, setRecipeToDelete] = useState<Recipe | null>(null);
	const [addModalOpen, setAddModalOpen] = useState(false);
	const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
	const [recipeToDuplicate, setRecipeToDuplicate] = useState<Recipe | null>(
		null,
	);
	const [pendingSelectId, setPendingSelectId] = useState<number | null>(null);

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "recipes-manager-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const { recipes, isLoading, error: queryError } = useRecipes();

	const productTypes = useMemo(
		() =>
			[
				...new Set(
					recipes
						.map((r) => r.ProductTypeName)
						.filter((name): name is string => !!name),
				),
			].sort((a, b) => a.localeCompare(b)),
		[recipes],
	);

	// Product lists the sizes of the chosen type, all of them when the type is All.
	const productSizes = useMemo(() => {
		const byId = new Map<number, string>();
		for (const recipe of recipes) {
			if (!recipe.ProductSizeId || !recipe.ProductSizeName) continue;
			if (productType && recipe.ProductTypeName !== productType) continue;
			byId.set(recipe.ProductSizeId, recipe.ProductSizeName);
		}
		return [...byId.entries()]
			.map(([Id, Name]) => ({ Id, Name }))
			.sort((a, b) => a.Name.localeCompare(b.Name));
	}, [recipes, productType]);

	// A link can name a type or a size the page no longer offers, and changing the type
	// leaves a size that does not belong to it.
	useDropUnknownValue(
		productType,
		!isLoading,
		productTypes,
		useCallback(() => dropValues("productType"), [dropValues]),
	);
	useDropUnknownValue(
		productSizeId,
		!isLoading,
		productSizes.map((size) => size.Id),
		useCallback(() => dropValues("productSizeId"), [dropValues]),
	);

	const filteredRecipes = useMemo(() => {
		const actives = onlyActives ? recipes.filter((r) => r.Active) : recipes;
		const byProduct = actives.filter(
			(r) =>
				(!productType || r.ProductTypeName === productType) &&
				(!productSizeId || r.ProductSizeId === productSizeId),
		);
		if (!searchText) return byProduct;
		const s = searchText.toLowerCase();
		return byProduct.filter(
			(r) =>
				r.Name?.toLowerCase().includes(s) ||
				r.ProductSizeName?.toLowerCase().includes(s) ||
				r.ProductTypeName?.toLowerCase().includes(s),
		);
	}, [recipes, searchText, onlyActives, productType, productSizeId]);

	// The id travels and the record is resolved from the list. A record being added has
	// no id yet, so it stays out of the URL.
	const selection = useUrlSelection<Recipe>({
		id: recipeId,
		setId: (value) => setFilters({ recipeId: value }),
		dropId: () => dropValues("recipeId"),
		rows: recipes,
		ready: !isLoading,
		getId: (record) => record.Id,
	});
	const selectedRecipe = selection.current;

	const { upsertRecipe, isUpserting } = useUpsertRecipe({
		onSuccess: (recipeId) => {
			const isNew = selection.isNew;
			setNotification(
				isNew ? "Recipe updated successfully" : "Recipe updated successfully",
			);
			if (recipeId) {
				const updated = recipes.find((r) => r.Id === recipeId);
				if (updated) {
					selection.adopt(updated);
				}
			}
		},
		onError: (err) => setError(err.message),
	});

	const { deleteRecipe, isDeleting } = useDeleteRecipe({
		onSuccess: () => {
			setNotification("Recipe deleted successfully");
			selection.clear();
			setDeleteModalOpen(false);
			setRecipeToDelete(null);
		},
		onError: (err) => setError(err.message),
	});

	const { createRecipeEmpty, isCreating } = useCreateRecipeEmpty({
		onSuccess: (recipeId) => {
			setNotification("Recipe created successfully");
			setAddModalOpen(false);
			if (recipeId) setPendingSelectId(recipeId);
		},
		onError: (err) => setError(err.message),
	});

	const { duplicateRecipe, isDuplicating } = useDuplicateRecipe({
		onSuccess: (recipeId) => {
			setNotification("Recipe duplicated successfully");
			setDuplicateModalOpen(false);
			setRecipeToDuplicate(null);
			if (recipeId) setPendingSelectId(recipeId);
		},
		onError: (err) => setError(err.message),
	});

	// The new recipe is not in the list until the refetch lands.
	useEffect(() => {
		if (pendingSelectId === null) return;
		const created = recipes.find((r) => r.Id === pendingSelectId);
		if (!created) return;
		selection.adopt(created);
		setPendingSelectId(null);
	}, [pendingSelectId, recipes, selection]);

	const handleRowSelected = useCallback(
		(recipe: Recipe | null) => {
			if (recipe) selection.select(recipe);
			else selection.clear();
		},
		[selection],
	);

	const handleDelete = useCallback((recipe: Recipe) => {
		setRecipeToDelete(recipe);
		setDeleteModalOpen(true);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (recipeToDelete?.Id) {
			deleteRecipe(recipeToDelete.Id);
		}
	}, [recipeToDelete, deleteRecipe]);

	const handleCancelDelete = useCallback(() => {
		setDeleteModalOpen(false);
		setRecipeToDelete(null);
	}, []);

	const handleAdd = useCallback(() => {
		setAddModalOpen(true);
	}, []);

	const handleAddSave = useCallback(
		(productSizeId: number, name: string) => {
			createRecipeEmpty({ productSizeId, name });
		},
		[createRecipeEmpty],
	);

	const handleSave = useCallback(
		(recipe: Recipe) => {
			upsertRecipe(recipe);
		},
		[upsertRecipe],
	);

	const handleClose = useCallback(() => {
		selection.clear();
	}, [selection]);

	const handleDuplicate = useCallback((recipe: Recipe) => {
		setRecipeToDuplicate(recipe);
		setDuplicateModalOpen(true);
	}, []);

	const handleDuplicateSave = useCallback(
		(recipeId: number, name: string) => {
			duplicateRecipe({ recipeId, name });
		},
		[duplicateRecipe],
	);

	if (queryError) {
		return <ErrorModal error={queryError.message} onHide={() => {}} />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				gap: 1,
				p: 1,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Recipes
			</Typography>

			<Paper elevation={3}>
				<RecipesToolbar
					onReset={() =>
						resetValues([
							"search",
							"onlyActives",
							"productType",
							"productSizeId",
						])
					}
					canReset={
						!isDefault("search", "onlyActives", "productType", "productSizeId")
					}
					search={searchText}
					onSearchChange={setSearchText}
					productType={productType}
					onProductTypeChange={(value) =>
						setFilters({ productType: value, productSizeId: null })
					}
					productTypes={productTypes}
					productSizeId={productSizeId}
					onProductSizeChange={(value) => setFilters({ productSizeId: value })}
					productSizes={productSizes}
					onlyActives={onlyActives}
					onOnlyActivesChange={(value) => setFilters({ onlyActives: value })}
					onAdd={handleAdd}
				/>
			</Paper>

			<Group
				id="recipes-manager-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							pr: 1,
						}}
					>
						<Paper
							elevation={3}
							sx={{ flex: 1, width: "100%", overflow: "hidden" }}
						>
							{isLoading ? (
								<CenteredLazyLoading />
							) : (
								<RecipesTable
									recipes={filteredRecipes}
									selectedRecipe={selectedRecipe?.Id ? selectedRecipe : null}
									onRowSelected={handleRowSelected}
									onDelete={handleDelete}
								/>
							)}
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 6,
							height: 40,
							borderRadius: 3,
							backgroundColor: "divider",
							"&:hover": { backgroundColor: "primary.main" },
						}}
					/>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper elevation={3} sx={{ height: "100%", overflow: "hidden" }}>
							<RecipeDetailViewer
								recipe={selectedRecipe}
								existingRecipes={recipes}
								isSaving={isUpserting || isDeleting}
								onSave={handleSave}
								onClose={handleClose}
								onDuplicate={handleDuplicate}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<AddRecipeModal
				open={addModalOpen}
				existingRecipes={recipes}
				onSave={handleAddSave}
				onCancel={() => setAddModalOpen(false)}
				isSaving={isCreating}
			/>

			<DuplicateRecipeModal
				open={duplicateModalOpen}
				existingRecipes={recipes}
				recipe={recipeToDuplicate}
				onDuplicate={handleDuplicateSave}
				onCancel={() => {
					setDuplicateModalOpen(false);
					setRecipeToDuplicate(null);
				}}
				isDuplicating={isDuplicating}
			/>

			<DeleteConfirmModal
				open={deleteModalOpen}
				title="DELETE RECIPE"
				itemName={recipeToDelete?.Name ?? ""}
				isDeleting={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
